import notHandledHere from './notHandledHere';
import withEntryCorrelationIds from './transactionEntryCorrelation';
import { constants, database } from 'lib_ui-services';

const _p = { database };
export const _private = _p;
export default {
    verb: 'doingCreate',
    type: 'transaction',
    excludedNamespaceRelations: [...notHandledHere, { namespace: 'print', relation: 'job' }],
    description: 'Update the transaction entry records on the database',
    priority: 10, // run this before the default doingCreate rule runs
    //this is the actual logic:
    logic
};

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").WorkflowStack} WorkflowStack
 * @typedef {import("rulesengine.io").Context} Context
 */

/**
 * @param {{
 *   data: T;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   workflowStack: WorkflowStack[];
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {T}
 */
async function logic({ data, context, dispatch }) {
    const {
        newRecord: { entries },
        changes,
        batchedFanOut
    } = data;
    const mergeRelation = context.relation.split('-')[0];
    // These per-entry updates always run: they are what makes the grid update the moment the user
    // hits Save, and they are what writes the optimistic local `{relation}-patch` record.  What
    // `batchedFanOut` changes is only whether each one ALSO queues its own
    // `PATCH /api/{namespace}/{relation}/{_id}`.  When the server applies the batch, they must not
    // -- 39 serialized round-trips out of the offline outbox is the 45 seconds this fixes, and
    // sending them anyway would apply every change twice (duplicate locationHistory, double
    // inventory adjustment).  willUpdate_namespace_relation reads this off the context.
    const transactionContext = { ...context, relation: mergeRelation, sentWithBatchedTransaction: !!batchedFanOut };
    const db = _p.database.get();
    // willCreate_namespace_relation_transaction already stamped these, and the batched wire
    // payload carries the same ids.  Re-deriving here would only matter for a caller that
    // reached doingCreate without going through that willCreate.
    const correlatedEntries = withEntryCorrelationIds(entries);
    const entryResults = await Promise.allSettled(
        correlatedEntries.map(async ({ _id, correlationId }) => {
            const result = await db.getById({ _id }, transactionContext);
            if (!result.length > 0) {
                throw new Error(
                    'Not supported:  Creating new records with transaction entries (as opposed to updates) is not yet supported.'
                );
            }
            const [oldRecord] = result;
            const newRecord = { ...oldRecord, ...changes };
            // Each entry must get its own correlationId.  `transactionContext` inherits the
            // one willCreate minted for the transaction record itself, and
            // willUpdate_namespace_relation only mints a correlationId when the context
            // doesn't already carry one.  Without this, every entry in the transaction is
            // saved under a single correlationId, and since the server uses it as the
            // `_id` of the `{relation}-patch` record, only the first entry keeps its audit
            // history -- the rest are discarded as duplicate keys, silently.
            await dispatch({ oldRecord, newRecord }, { ...transactionContext, verb: 'update', correlationId }, true);
        })
    );
    entryResults.forEach((result, i) => {
        if (result.status === 'rejected') {
            const entry = correlatedEntries[i];
            // the entry's title field, whatever it is called -- the only other property on an
            // entry besides its _id and the correlationId stamped on by willCreate.
            const [, titleAlternative] =
                Object.entries(entry).find(([key]) => key !== '_id' && key !== 'correlationId') || [];
            dispatch(
                {
                    isError: true, //todo: gl2025 remove
                    variant: 'error',
                    type: constants.notificationTypes.TOAST,
                    message: `${titleAlternative} update failed.  ${result.reason.message}`
                },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
        }
    });
    return { ...data, entryResults };
}
